import 'focus-visible';

import Application from '@ember/application';
import loadInitializers from 'ember-load-initializers';
import Resolver from 'ember-resolver';
import * as Sentry from '@sentry/ember';
import config from 'dashboard/config/environment';
import { importSync, isDevelopingApp, macroCondition } from '@embroider/macros';

/**
 * Nothing in our codebase points to Ember 4.12, but the inspector doesn't work without this at this point.
 * @see https://github.com/emberjs/ember-inspector/issues/2612#issuecomment-3370475364
 */
import setupInspector from '@embroider/legacy-inspector-support/ember-source-4.12';

if (macroCondition(isDevelopingApp())) {
  importSync('./deprecation-workflow');

  // Start Mirage in development unless MIRAGE_ENABLED=false (e.g. start:qa)
  // @see https://github.com/bgantzler/ember-mirage/blob/main/docs/migration.md
  if (config.APP.mirage?.enabled) {
    const { default: makeServer } = importSync('dashboard/mirage/config') as {
      default: (config?: Record<string, unknown>) => unknown;
    };
    makeServer();
  }
}

// import Shoelace web components
import './shoelace';

if (config.environment === 'production') {
  /**
   * Sentry recommends all static SDK configuration be done here, in app.js,
   * and only using config/environment.js when needed.
   * @see https://github.com/getsentry/sentry-javascript/tree/master/packages/ember#usage
   */
  Sentry.init({
    dsn: 'https://d2bd0b83b25e44c3b3a1d11e8342cd25@o78392.ingest.sentry.io/5702713',
    environment: config.environment,
    // https://docs.sentry.io/platforms/javascript/configuration/releases/#bind-the-version
    release: 'com.showbie.admin@' + config.APP.version,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production,
    tracesSampleRate: 1.0,

    /** @see https://docs.sentry.io/platforms/javascript/guides/ember/configuration/filtering/#using-ignore-errors */
    ignoreErrors: ['Request timeout ToolbarStatus'],

    /**
     * @see https://docs.sentry.io/platforms/javascript/guides/ember/configuration/filtering/#using-allowurls-and-denyurls
     **/
    allowUrls: [/^https:\/\/((admin|adminstaging|adminqa)\.)showbie\.com/],

    /**
     * If the entire session is not sampled, use the below sample rate to sample
     * sessions when an error occurs.
     * @see https://docs.sentry.io/platforms/javascript/session-replay/
     * @see https://docs.sentry.io/platforms/javascript/guides/ember/session-replay
     **/
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      /**
       * Note that all content and media are masked by default to avoid capturing sensitive information (PII).
       * @see https://docs.sentry.io/platforms/javascript/session-replay/configuration/#privacy-configuration
       **/
      Sentry.replayIntegration(),
    ],
  });
}

export default class App extends Application {
  modulePrefix = config.modulePrefix;
  podModulePrefix = config.podModulePrefix;
  Resolver = Resolver;

  /** @see https://github.com/embroider-build/embroider/tree/4be2b8e81f3f87a08247e5898cafb35e919b9cbf/packages/legacy-inspector-support */
  inspector = setupInspector(this);
}

loadInitializers(App, config.modulePrefix);

function setDocHeight() {
  document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`);
}

window.addEventListener('resize', setDocHeight);
window.addEventListener('orientationchange', setDocHeight);

setDocHeight();
